import { BrandInterface, LocationPageInterface, LocationScope, LocationVarKey } from "@swivl-mono/types";
import { Button, ButtonColor } from "@swivl-mono/ui";
import { useMemo } from "react";
import QRCode from "react-qr-code";
import { DropdownRow, InputRow } from "../../../../Components/Worksheets/WorksheetComponent/WorksheetComponents";
import { useMergedLocationVariables } from "../../../../Hooks/useLocationVariables";
import { AlertsModel } from "../../../../Models/Notifications/Alerts.Model";
import LocationVarsModel from "../../../../Models/Variables/LocationVars.Model";

interface Props {
  page: LocationPageInterface;
  brands?: { [brandId: number]: BrandInterface };
}

export function SpotSidebarControls(props: Props) {
  const { page, brands } = props;
  const vars = useMergedLocationVariables(
    [LocationVarKey.SpotBrandSlug, LocationVarKey.SpotLinkSlug, LocationVarKey.SpotLocationSlug],
    page.groupId,
    page.locationId,
  );

  const brand = useMemo(() => {
    if (brands && vars && vars[LocationVarKey.SpotBrandSlug]) {
      return Object.values(brands).find((brand) => brand.spotSlug === vars[LocationVarKey.SpotBrandSlug].value);
    }
  }, [brands, page.locationId]);

  const downloadSVG = () => {
    const svg = document.getElementById("qrcode");

    if (!svg) {
      return;
    }
    const filename = vars ? vars[LocationVarKey.SpotLocationSlug]?.value : undefined;
    const svgData = new XMLSerializer().serializeToString(svg as Node);
    const downloadLink = document.createElement("a");
    downloadLink.download = filename || "qrcode";
    downloadLink.href = "data:image/svg+xml;base64," + btoa(svgData);
    downloadLink.click();
  };

  const url =
    page.type === LocationScope.LOCATION && brand && vars && vars[LocationVarKey.SpotLinkSlug]
      ? "https://my.spot.is/" + brand.spotLinkSlug + "/" + vars[LocationVarKey.SpotLinkSlug].value + "/qr"
      : undefined;

  return (
    <div className="p-5">
      <div className="text-lg font-bold">Spot</div>
      <div className="text-sm text-gray-500">Drag and drop buttons to add them to the spot</div>

      {page.type === LocationScope.LOCATION && <InputRow {...page} marginTop brand={brand} label="Spot Location URL" keyName={LocationVarKey.SpotLocationSlug}
        shouldSaveCallback={async (value) => {
          const isLocationSlugUnique = await LocationVarsModel.getIsLocationSlugUnique(brand?.spotSlug!, page.locationId!, value);

          if (!isLocationSlugUnique) {
            AlertsModel.errorToast("Location URL already exists");
            return false;
          }
          return true;
        }}
      />}
      {page.type === LocationScope.LOCATION && (
        <InputRow {...page} marginTop label="Spot Location Short Link URL" brand={brand} shouldSaveCallback={async (value) => {
          const isLocationSlugUnique = await LocationVarsModel.getIsLocationSlugUnique(brand?.spotSlug!, page.locationId!, undefined, value);

          if (!isLocationSlugUnique) {
            AlertsModel.errorToast("Location Short Link URL already exists");
            return false;
          }
          return true;
        }} keyName={LocationVarKey.SpotLinkSlug} />
      )}

      {brands && (
        <DropdownRow
          {...page}
          marginTop
          label="Brand"
          keyName={LocationVarKey.SpotBrandSlug}
          options={Object.values(brands).map((brand) => ({ label: brand.name, value: brand.spotSlug }))}
        />
      )}

      {/* {page.type === LocationScope.ORG && <div className="text-sm text-gray-500">The short link URL will be used to create a QR code for the location.</div>} */}

      {url && (
        <>
          <div className="mb-3">
            <label>Short Link</label>
            <a href={url} target="_blank" rel="noreferrer">
              {url}
            </a>
          </div>
          <div className="mb-3">
            <label>QR Code</label>
            <QRCode id="qrcode" size={256} style={{ height: "auto", maxWidth: "100%", width: "100%" }} value={url} viewBox={`0 0 256 256`} />
          </div>
          <Button color={ButtonColor.ORANGE} onClick={downloadSVG}>
            Download QR Code
          </Button>
        </>
      )}
    </div>
  );
}
